import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signUp: (email: string, password: string, fullName?: string, username?: string) => Promise<{ error: { message: string } | null }>;
  signIn: (email: string, password: string) => Promise<{ error: { message: string } | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Memoized so useIdleTimeout's effect doesn't reset listeners/timers every render.
  const handleIdleTimeout = useCallback(async () => {
    if (user) {
      toast.info('Sesi Anda telah berakhir karena tidak ada aktivitas. Silakan login kembali.');
      await signOut();
    }
  }, [user]);

  // Setup idle timeout - only enabled when user is logged in
  useIdleTimeout({
    onIdle: handleIdleTimeout,
    idleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!user
  });

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // If the event is SIGNED_OUT, ensure we clear everything
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          setIsAdmin(false);
          setLoading(false);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
          
          // Check admin status and update last_login for any login event
          if (session?.user) {
            checkAdminStatus(session.user.id);
            if (event === 'SIGNED_IN') {
              supabase
                .from('profiles')
                .update({ last_login: new Date().toISOString() })
                .eq('id', session.user.id)
                .then(() => {});
            }
          } else {
            setIsAdmin(false);
          }
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      // If there's an error getting the session, clear everything
      if (error) {
        console.error('Session error:', error);
        setSession(null);
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        checkAdminStatus(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();
      
      setIsAdmin(!error && !!data);
    } catch {
      setIsAdmin(false);
    }
  };

  const signUp = async (email: string, password: string, fullName?: string, username?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName || '',
          username: username || ''
        }
      }
    });

    // If there's an auth error, return it
    if (error) {
      // Check if it's a duplicate username error from the trigger
      if (error.message && error.message.includes('duplicate key value violates unique constraint "profiles_username_key"')) {
        return { 
          error: { 
            message: 'Username tersebut sudah digunakan. Silakan pilih username lain.' 
          } 
        };
      }
      return { error };
    }

    return { error: null };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) return { error };
    
    // Check if account is disabled
    if (data.user) {
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('status')
          .eq('id', data.user.id)
          .single();
        
        if (profileError) {
          console.error('Error checking account status:', profileError);
        } else if (profile?.status === 'disabled') {
          // Sign out immediately if account is disabled
          await supabase.auth.signOut();
          return { 
            error: { 
              message: 'Akun Anda telah dinonaktifkan karena tidak aktif dalam jangka waktu yang lama atau memiliki kendala. Silakan hubungi owner untuk informasi lebih lanjut.' 
            } 
          };
        }
        
        // Update last_login timestamp
        await supabase
          .from('profiles')
          .update({ last_login: new Date().toISOString() })
          .eq('id', data.user.id);
      } catch (loginError) {
        console.error('Error during login check:', loginError);
      }
    }
    
    return { error: null };
  };

  /**
   * Removes any leftover Supabase keys.
   *
   * Guarded: localStorage throws in private windows and when a browser blocks site
   * data. Previously this ran unguarded in both the try and the catch, so a throw in
   * the try was followed by the same throw in the catch — leaving the error uncaught
   * and skipping the redirect.
   */
  const clearSupabaseStorage = () => {
    try {
      for (const key of Object.keys(window.localStorage)) {
        if (key.startsWith('sb-')) {
          window.localStorage.removeItem(key);
        }
      }
    } catch {
      // Storage unavailable — there is nothing persisted to clear.
    }
  };

  const signOut = async () => {
    try {
      // Clear local state first
      setUser(null);
      setSession(null);
      setIsAdmin(false);

      // Sign out from Supabase (this also clears its own storage)
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      // Runs whether or not the sign-out call succeeded.
      clearSupabaseStorage();

      // Redirect to home page and force reload to clear all state
      window.location.href = '/';
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
